import React, { useState, useEffect, useRef } from "react";
import AnimatedFAQSection from "../components/AnimatedFAQSection";
import ReviewsCarousel from "../components/ReviewsCarousel";
import { ProductSection } from "../components/ProductSection";
import { ArrowRight } from "lucide-react"; // or the correct path to ArrowRight component
import IngredientsSection from "../components/IngredientsSection";

export default function Home() {
  const imageRef = useRef(null);
  const [browserHeight, setBrowserHeight] = useState("min-h-screen");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Add a check for the element
          if (!entry.target) return;

          if (entry.isIntersecting) {
            // Add classes with a slight delay to ensure proper animation
            setTimeout(() => {
              entry.target.classList.add("translate-y-0", "opacity-100");
              entry.target.classList.remove("translate-y-10", "opacity-0");
            }, 100);
          } else {
            // Optional: Reset animation when scrolling back up
            entry.target.classList.remove("translate-y-0", "opacity-100");
            entry.target.classList.add("translate-y-10", "opacity-0");
          }
        }
      },
      {
        threshold: 0.1,
        // Add root margin to trigger slightly before the element comes into view
        rootMargin: "50px",
      }
    );

    const currentImageRef = imageRef.current;
    if (currentImageRef) {
      // Ensure initial state
      currentImageRef.classList.add("translate-y-10", "opacity-0");
      observer.observe(currentImageRef);
    }

    return () => {
      if (currentImageRef) {
        observer.unobserve(currentImageRef);
      }
    };
  }, []);

  const scrollToProduct = () => {
    const element = document.getElementById("product");
    if (element) {
      const offset = 100; // Adjust this value to change how much space you want from the top
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const detectBrowser = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

      // Check if it's a mobile device
      const isMobile = window.innerWidth <= 768; // Adjust breakpoint as needed

      if (isSafari && isMobile) {
        setBrowserHeight("min-h-[90vh]"); // 80% height for Safari on mobile
      } else if (isMobile) {
        setBrowserHeight("min-h-[95vh]"); // 90% height for other browsers on mobile
      } else {
        setBrowserHeight("min-h-screen"); // full screen height for desktop
      }
    };

    // Run on mount
    detectBrowser();

    // Rerun on resize to handle orientation changes
    window.addEventListener("resize", detectBrowser);

    // Cleanup
    return () => {
      window.removeEventListener("resize", detectBrowser);
    };
  }, []);

  return (
    <div className={`${browserHeight}`}>
      {/* Hero Section - FIXED: Added negative margin and bg color */}
      <section
        className={`relative md:mt-0 pt-8 md:pt-60 
        pb-12 ${browserHeight}
        md:pb-20 overflow-hidden 
         [@media(min-height:1000px)]:xl:min-h-[70vh] 
        mb-[-2px] bg-[#e9b420]`}
      >
        {/* Video Background Container - FIXED: Increased height to 101% */}
        <div className="absolute top-0 right-0 left-0 bottom-0 w-full h-[100%]  block overflow-hidden">
          {/* Desktop Video - FIXED: Increased height to 101% */}
          <video
            className="absolute w-full h-[101%] object-cover hidden lg:block"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/videos/zivbio-desktop-video.mp4" type="video/mp4" />
          </video>

          {/* Mobile Video - FIXED: Increased height to 101% */}
          <video
            className="absolute w-full h-[100%] object-cover lg:hidden block"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/videos/zivbio-mobile-video.mp4" type="video/mp4" />
          </video>

          {/* Fallback image if video fails to load */}
          <img
            src="/backgrounds/hero-image.jpg"
            alt="Hero Background"
            className="absolute w-full h-full object-cover hidden"
          />
        </div>

        {/* ADDED: Thin bar to ensure no gap */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-[#e9b420] z-20" />

        {/* Mobile background */}
        <div
          className="absolute top-0 right-0 w-full h-1/2 bg-[#e9b320] hidden"
          style={{
            clipPath: "polygon(0% 0, 100% 0, 100% 100%)",
          }}
        />

        <div className="container absolute bottom-[5%] xl:bottom-[20%] left-[0%] xl:left-[3%] w-full xl:w-1/2 mx-auto px-4">
          {/* Main content wrapper - reverse flex on mobile */}
          <div className="flex flex-col-reverse lg:flex-row items-center gap-8 md:gap-12 relative z-10">
            {/* Text Content - centered on mobile with padding */}
            <div className="flex-1 text-center lg:text-left pt-8 sm:pt-12 md:pt-0">
              <h1 className="text-5xl sm:text-4xl md:text-6xl xl:text-9xl tracking-wider mb-4 md:mb-6">
                <span
                  className="text-[#4d1312] inline-block sm:[text-shadow:3px_0_0_#fcf5d4,3px_3px_0_#fcf5d4] [text-shadow:1px_0_0_#fcf5d4,1px_1px_0_#fcf5d4]"
                  style={{
                    fontFamily: "Headlined, sans-serif",
                  }}
                >
                  IGNITE YOUR PRIME
                </span>
              </h1>
              <p
                className="text-[#4d1312] inline-block text-xl sm:text-lg md:text-4xl xl:text-6xl mb-6 md:mb-8 max-w-2xl mx-auto lg:mx-0 [text-shadow:1px_0_0_white,1px_1px_0_white,1px_1px_0_white,0px_1px_0_white] sm:[text-shadow:2px_0_0_white,2px_2px_0_white,2px_1px_0_white,0px_1px_0_white] md:[text-shadow:3px_0_0_white,3px_3px_0_white,3px_1px_0_white,0px_2px_0_white] xl:[text-shadow:4px_0_0_white,4px_4px_0_white,4px_2px_0_white,0px_3px_0_white]"
                style={{
                  fontFamily: "Lobster, sans-serif",
                }}
              >
                Premium Testosterone Booster For Men
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  type="button"
                  onClick={scrollToProduct}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-primary rounded-lg font-bold text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg text-sm sm:text-base"
                >
                  Buy Now <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Main Section */}
      <ProductSection />

      <div className="w-full">
        {/* First Section - Modern Man */}
        <div className="relative w-full mb-2 overflow-hidden">
          <div className="absolute inset-0 bg-[#e9b420] md:bg-[#a41e24]" />
          <div
            className="absolute top-0 right-[33%] w-[30.1%] h-full bg-[#e9b420] hidden md:block"
            style={{
              clipPath: "polygon(100% 0, 0 0, 100% 100%)",
              transform: "translateX(1px)",
            }}
          />
          <div className="absolute top-0 right-0 w-[33.34%] h-full bg-[#e9b420] hidden md:block" />
          <div className="relative z-10 container mx-auto px-4 pt-8 md:py-0 h-full">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12">
              <div className="w-full md:pr-12 md:pt-16 lg:pt-20 text-center md:text-left">
                <h2
                  className="text-[3.2rem] sm:text-5xl md:text-5xl lg:text-6xl xl:text-8xl [@media(min-width:1550px)]:xl:text-[6.7rem] text-white mb-4 md:mb-6 lg:mb-8 tracking-wider leading-none [text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] md:[text-shadow:2px_0_0_#4d1312,4px_0_0_#4d1312,2px_2px_0_#4d1312,4px_4px_0_#4d1312]"
                  style={{
                    fontFamily: "Headlined, sans-serif",
                  }}
                >
                  DESIGNED FOR
                  <br />
                  THE MODERN MAN
                </h2>
                <p
                  className="text-white text-[1.2rem] sm:text-xl  md:text-3xl lg:text-4xl xl:text-5xl [@media(min-width:1550px)]:xl:text-[3.5rem] mb-4 md:mb-6 lg:mb-8 leading-relaxed [@media(min-width:1550px)]:xl:leading-[130%] uppercase tracking-wider md:tracking-wide [text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] md:[text-shadow:2px_0_0_#4d1312,4px_0_0_#4d1312,2px_2px_0_#4d1312,4px_4px_0_#4d1312]"
                  style={{
                    fontFamily: "Veneer, sans-serif",
                    // wordSpacing: "0.1em",
                  }}
                >
                  OUR FORMULA IS DESIGNED TO&nbsp;
                  <br className="hidden md:block" />
                  BOOST ENERGY, ENHANCE LIBIDO&nbsp;
                  <br className="hidden md:block" />
                  AND PROMOTE MUSCLE GROWTH.
                </p>
                <p
                  className="md:text-yellow-400 text-white text-xl sm:text-2xl md:text-2xl lg:text-4xl xl:text-5xl [@media(min-width:1550px)]:xl:text-[3.7rem] leading-relaxed [@media(min-width:1550px)]:xl:leading-[100%] [text-shadow:1px_0_0_#4d1312,1px_0_0_#4d1312,1px_1px_0_#4d1312,1px_1px_0_#4d1312] md:[text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] [@media(min-width:1550px)]:[text-shadow:4px_4px_0_#4d1312]"
                  style={{
                    fontFamily: "Lobster, sans-serif",
                  }}
                >
                  Feel more confident, driven,
                  <br className="hidden md:block" />
                  and revitalized.
                </p>
              </div>
              <div className="w-full">
                <img
                  src="/media/zivbio-man1.png"
                  alt="Modern Man"
                  className="h-[400px] sm:h-[400px] md:mb-[-20px] lg:mb-[-9px] xl:mb-[-5px] md:h-[500px] lg:h-[800px] object-contain mx-auto md:mx-0 md:ml-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Second Section - Experience */}
        <div className="relative w-full mb-2 xl:min-h-[600px] overflow-hidden">
          <div className="absolute inset-0 bg-[#a41e24]" />
          <div
            className="absolute top-0 right-[33%] w-[30.1%] h-full bg-[#e9b420] hidden md:block"
            style={{
              clipPath: "polygon(100% 0%, 100% 100%, 0% 100%)",
              transform: "translateX(1px)",
            }}
          />
          <div className="absolute top-0 right-0 w-[33.34%] h-full bg-[#e9b420] hidden md:block" />
          <div className="relative z-10 container mx-auto px-4 py-8 md:py-0 h-full">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="w-full md:w-3/4 lg:w-3/4">
                <img
                  src="/media/zivbio-man2.png"
                  alt="Experience"
                  className="h-[300px] sm:h-[400px] md:mb-[-20px] lg:mb-[-85px] xl:mb-[-5px] md:h-[500px] lg:h-[800px] object-contain mx-auto md:ml-0"
                />
              </div>
              <div className="w-full md:w-3/4 lg:w-3/4 py-0 order-1 md:order-2 text-center md:text-right">
                <h2
                  className="text-[3.2rem] sm:text-5xl md:text-5xl lg:text-6xl xl:text-8xl [@media(min-width:1550px)]:xl:text-[6.7rem] text-white mb-4 md:mb-6 tracking-wider leading-tight bg-[#e9b420] md:bg-transparent -mt-8 md:mt-0 w-[115%] md:w-auto -ml-5 md:ml-0 p-3 pt-5 md:p-0 md:pt-0 [text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] md:[text-shadow:2px_0_0_#4d1312,4px_0_0_#4d1312,2px_2px_0_#4d1312,4px_4px_0_#4d1312]"
                  style={{
                    fontFamily: "Headlined, sans-serif",
                  }}
                >
                  EXPERIENCE
                  <br />
                  THE DIFFERENCE
                </h2>
                <div className="flex flex-col items-center md:items-end gap-6 md:gap-8 lg:gap-16">
                  <div className="text-center md:text-right">
                    <p
                      className="text-white text-lg sm:text-xl md:text-2xl tracking-wider lg:text-4xl xl:text-5xl mb-6 md:mb-8 lg:mb-12 leading-relaxed [@media(min-width:1550px)]:xl:leading-[130%] [text-shadow:1px_0_0_#4d1312,2px_0_0_#4d1312,1px_1px_0_#4d1312,2px_2px_0_#4d1312] md:[text-shadow:2px_0_0_#4d1312,4px_0_0_#4d1312,2px_2px_0_#4d1312,4px_4px_0_#4d1312]"
                      style={{
                        fontFamily: "Veneer, sans-serif",
                        wordSpacing: "0.1em",
                      }}
                    >
                      MORE THAN JUST A BOOST. EXPERIENCE&nbsp;
                      <br className="hidden md:block" />
                      COMPREHENSIVE SUPPORT FOR MUSCLE,&nbsp;
                      <br className="hidden md:block" />
                      ENERGY AND LIBIDO, DESIGNED FOR A&nbsp;
                      <br className="hidden md:block" />
                      DIFFERENCE YOU CAN FEEL.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center md:justify-end gap-6 md:gap-8 lg:gap-16">
                  <div className="text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-16 md:h-16 lg:w-20 lg:h-20 flex items-center justify-center mb-3">
                      <img
                        src="/media/icon_cirlce_man-muscle.svg"
                        alt="Muscle"
                        className="w-full h-full"
                      />
                    </div>
                    <p
                      className="md:text-[#4d1312] text-white text-xl sm:text-xl md:text-2xl lg:text-3xl tracking-wider font-bold [text-shadow:1px_0_0_#4d1312,1px_0_0_#4d1312,1px_1px_0_#4d1312,1px_1px_0_#4d1312] md:[text-shadow:2px_2px_0_#fcf5d4]"
                      style={{
                        fontFamily: "Veneer, sans-serif",
                      }}
                    >
                      MUSCLE
                      <br />
                      GROWTH
                      <sup
                        style={{
                          top: "-1em",
                          fontSize: "50%",
                        }}
                      >
                        †
                      </sup>
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-16 md:h-16 lg:w-20 lg:h-20 flex items-center justify-center mb-3">
                      <img
                        src="/media/icon_cirlce_spark.svg"
                        alt="Energy"
                        className="w-full h-full"
                      />
                    </div>
                    <p
                      className="md:text-[#4d1312] text-white text-xl sm:text-xl md:text-2xl lg:text-3xl tracking-wider font-bold [text-shadow:1px_0_0_#4d1312,1px_0_0_#4d1312,1px_1px_0_#4d1312,1px_1px_0_#4d1312] md:[text-shadow:2px_2px_0_#fcf5d4]"
                      style={{
                        fontFamily: "Veneer, sans-serif",
                      }}
                    >
                      ENERGY
                      <br />
                      LEVELS
                      <sup
                        style={{
                          top: "-1em",
                          fontSize: "50%",
                        }}
                      >
                        †
                      </sup>
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-16 md:h-16 lg:w-20 lg:h-20 flex items-center justify-center mb-3">
                      <img
                        src="/media/icon_cirlce_male-symbol.svg"
                        alt="Libido"
                        className="w-full h-full"
                      />
                    </div>
                    <p
                      className="md:text-[#4d1312] text-white text-xl sm:text-xl md:text-2xl lg:text-3xl tracking-wider font-bold [text-shadow:1px_0_0_#4d1312,1px_0_0_#4d1312,1px_1px_0_#4d1312,1px_1px_0_#4d1312] md:[text-shadow:2px_2px_0_#fcf5d4]"
                      style={{
                        fontFamily: "Veneer, sans-serif",
                      }}
                    >
                      LIBIDO &
                      <br />
                      STAMINA
                      <sup
                        style={{
                          top: "-1em",
                          fontSize: "50%",
                        }}
                      >
                        †
                      </sup>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Section - Ingredients */}
        <IngredientsSection />
      </div>

      {/* reviews Section*/}
      <ReviewsCarousel />

      {/* FAQ Section */}
      <AnimatedFAQSection />
    </div>
  );
}
